import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertTriangle, FileSearch, Layers, Ruler, GitMerge, Wrench, Info, ScrollText } from "lucide-react";

/**
 * DrawingReconciliation — Fictiv-style panel that surfaces the declared
 * requirements parsed from the uploaded drawing and flags conflicts with
 * the current wizard configuration. It is purely presentational: the
 * parent owns the state and decides how to react to user confirmations.
 *
 * Props:
 *  - requirements: object from /quotes/extract-drawing-requirements
 *  - features: optional geometry_features (B-Rep from OCP) for 3D models
 *  - materials: catalog list (for matching the detected material)
 *  - selectedMaterialId: current selection in step 2 (may be empty)
 *  - onApplyMaterial: callback(id) — user clicks "Use detected material"
 *  - loading: bool — show skeleton while extraction is in flight
 */

const FAMILY_KEYS = [
  "stainless_steel", "aluminum", "carbon_steel", "brass", "bronze",
  "titanium", "copper", "plastic", "tool_steel", "inconel",
];

function matchMaterial(materials, callout) {
  if (!callout) return null;
  const fam = callout.normalized_family;
  const grade = (callout.grade_hint || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!fam && !grade) return null;
  // Prefer exact grade match if the catalog material's name contains the grade
  if (grade) {
    const exact = materials.find((m) =>
      (m.name || "").toLowerCase().replace(/[^a-z0-9]/g, "").includes(grade)
    );
    if (exact) return exact;
  }
  // Otherwise fall back to family match by category text.
  const famKeywords = {
    stainless_steel: ["inox", "stainless"],
    aluminum: ["allumin", "aluminum"],
    carbon_steel: ["carbonio", "carbon", "c40", "c45"],
    brass: ["ottone", "brass"],
    bronze: ["bronzo", "bronze"],
    titanium: ["titanio", "titanium"],
    copper: ["rame", "copper"],
    plastic: ["plast", "peek", "delrin", "pa6", "nylon", "abs", "policarbonato"],
    tool_steel: ["utensili", "tool steel"],
    inconel: ["inconel"],
  };
  const kws = famKeywords[fam] || [];
  if (kws.length === 0) return null;
  return materials.find((m) => {
    const hay = `${m.name} ${m.category}`.toLowerCase();
    return kws.some((k) => hay.includes(k));
  }) || null;
}

function Tag({ children, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-800/60 border-slate-700 text-slate-300",
    blue: "bg-blue-500/10 border-blue-500/40 text-blue-300",
    emerald: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300",
    amber: "bg-amber-500/10 border-amber-500/40 text-amber-300",
    cyan: "bg-cyan-500/10 border-cyan-500/40 text-cyan-300",
  };
  return (
    <span data-testid={testid} className={`text-[11px] px-2 py-0.5 rounded border font-mono-data ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function Row({ icon: Icon, label, children, testid }) {
  return (
    <div className="flex items-start gap-3 py-2" data-testid={testid}>
      <Icon size={14} className="mt-0.5 text-slate-400 flex-shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="label-tech text-[10px] text-slate-400 mb-1">{label}</div>
        <div className="text-sm text-slate-200 flex flex-wrap gap-1.5 items-center">{children}</div>
      </div>
    </div>
  );
}

export default function DrawingReconciliation({
  requirements,
  features,
  materials = [],
  selectedMaterialId = "",
  onApplyMaterial,
  loading = false,
}) {
  const { t } = useTranslation();
  const matched = useMemo(
    () => matchMaterial(materials, requirements?.material_callout),
    [materials, requirements?.material_callout]
  );

  if (loading) {
    return (
      <div className="tech-card p-5" data-testid="drawing-recon-loading">
        <div className="flex items-center gap-2 mb-3">
          <FileSearch size={14} className="text-blue-400 animate-pulse"/>
          <div className="label-tech text-blue-300">{t("drawingRecon.title")}</div>
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-slate-800 rounded animate-pulse w-3/4"/>
          <div className="h-3 bg-slate-800 rounded animate-pulse w-1/2"/>
          <div className="h-3 bg-slate-800 rounded animate-pulse w-2/3"/>
        </div>
        <div className="mt-3 text-xs text-slate-500">
          {t("drawingRecon.loadingBody")}
        </div>
      </div>
    );
  }

  if (!requirements) return null;

  const mat = requirements.material_callout || {};
  const tol = requirements.tolerances || {};
  const fin = requirements.surface_finish || {};
  const threads = requirements.threads || [];
  const gdt = requirements.gdt_callouts || [];
  const treatments = requirements.treatments_or_coatings || [];
  const notes = requirements.drawing_notes || [];

  const confidence = requirements.confidence || "medium";
  const confTone = confidence === "high" ? "emerald" : confidence === "low" ? "amber" : "blue";
  const confLabel = confidence === "high" ? t("drawingRecon.confHigh") : confidence === "low" ? t("drawingRecon.confLow") : t("drawingRecon.confMedium");

  const hasMaterialCallout = !!(mat.raw_text || mat.normalized_family);
  const materialConflict = hasMaterialCallout && matched && selectedMaterialId && selectedMaterialId !== matched.id;
  const materialNotInCatalog = hasMaterialCallout && !matched;

  // Compute number of insights so we can show empty-state UX.
  const insightCount =
    (hasMaterialCallout ? 1 : 0) +
    (tol.general_class || tol.tightest_callout_mm || (tol.linear_callouts || []).length ? 1 : 0) +
    (fin.ra_um || (fin.callouts || []).length ? 1 : 0) +
    threads.length +
    gdt.length +
    treatments.length +
    notes.length;

  return (
    <div className="tech-card p-5" data-testid="drawing-recon-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GitMerge size={14} className="text-blue-400"/>
          <div className="label-tech text-blue-300">{t("drawingRecon.title")}</div>
        </div>
        <Tag tone={confTone} testid="drawing-recon-confidence">{confLabel}</Tag>
      </div>

      {(requirements.drawing_number || requirements.revision || requirements.scale) && (
        <div className="text-[11px] text-slate-500 font-mono-data mb-3 flex gap-3">
          {requirements.drawing_number && <span data-testid="drawing-number">N° {requirements.drawing_number}</span>}
          {requirements.revision && <span data-testid="drawing-revision">Rev. {requirements.revision}</span>}
          {requirements.scale && <span data-testid="drawing-scale">{t("drawingRecon.scale")} {requirements.scale}</span>}
        </div>
      )}

      {insightCount === 0 ? (
        <div className="flex items-start gap-2 px-3 py-3 rounded bg-slate-800/50 border border-slate-700 text-xs text-slate-400" data-testid="drawing-recon-empty">
          <Info size={14} className="mt-0.5 text-slate-500"/>
          <div>
            {t("drawingRecon.noCallouts")}
          </div>
        </div>
      ) : (
        <div className="divide-y divide-slate-800">
          {/* MATERIAL — most important row, with apply / conflict UX */}
          {hasMaterialCallout && (
            <Row icon={Layers} label={t("drawingRecon.materialDeclared")} testid="recon-row-material">
              {mat.raw_text && <span className="font-medium text-slate-100">{mat.raw_text}</span>}
              {mat.normalized_family && (
                <Tag tone="slate">{FAMILY_KEYS.includes(mat.normalized_family) ? t(`drawingRecon.families.${mat.normalized_family}`) : mat.normalized_family}</Tag>
              )}
              {matched && !materialConflict && !materialNotInCatalog && (
                <Tag tone="emerald" testid="recon-material-matched">{t("drawingRecon.mappedTo", { name: matched.name })}</Tag>
              )}
              {materialConflict && (
                <Tag tone="amber" testid="recon-material-conflict">{t("drawingRecon.conflict")}</Tag>
              )}
              {materialNotInCatalog && (
                <Tag tone="amber" testid="recon-material-missing">{t("drawingRecon.notInCatalog")}</Tag>
              )}
              {matched && onApplyMaterial && selectedMaterialId !== matched.id && (
                <button
                  type="button"
                  onClick={() => onApplyMaterial(matched.id)}
                  className="text-[11px] px-2 py-0.5 rounded border border-blue-500/60 text-blue-300 hover:bg-blue-500/10 transition-colors"
                  data-testid="recon-apply-material"
                >
                  {t("drawingRecon.useMaterial", { name: matched.name })}
                </button>
              )}
            </Row>
          )}

          {(tol.general_class || tol.tightest_callout_mm || (tol.linear_callouts || []).length > 0) && (
            <Row icon={Ruler} label={t("drawingRecon.tolerances")} testid="recon-row-tolerances">
              {tol.general_class && <Tag tone="blue">{tol.general_class}</Tag>}
              {tol.tightest_callout_mm != null && (
                <Tag tone="cyan">{t("drawingRecon.minTol", { v: tol.tightest_callout_mm })}</Tag>
              )}
              {(tol.linear_callouts || []).slice(0, 6).map((c, i) => (
                <Tag key={`tol-${i}`}>{c}</Tag>
              ))}
            </Row>
          )}

          {(fin.ra_um || (fin.callouts || []).length > 0) && (
            <Row icon={Layers} label={t("drawingRecon.surfaceFinish")} testid="recon-row-finish">
              {fin.ra_um != null && <Tag tone="cyan">Ra {fin.ra_um} µm</Tag>}
              {(fin.callouts || []).slice(0, 4).map((c, i) => (
                <Tag key={`fin-${i}`}>{c}</Tag>
              ))}
            </Row>
          )}

          {threads.length > 0 && (
            <Row icon={Wrench} label={t("drawingRecon.threads", { count: threads.length })} testid="recon-row-threads">
              {threads.slice(0, 6).map((th, i) => (
                <Tag key={`th-${i}`} tone="blue">
                  {th.designation}
                  {th.quantity ? ` ×${th.quantity}` : ""}
                  {th.depth_mm ? ` · ${th.depth_mm} mm` : ""}
                </Tag>
              ))}
            </Row>
          )}

          {gdt.length > 0 && (
            <Row icon={Ruler} label={`GD&T (${gdt.length})`} testid="recon-row-gdt">
              {gdt.slice(0, 8).map((g, i) => (
                <Tag key={`gdt-${i}`} tone="cyan">{g}</Tag>
              ))}
            </Row>
          )}

          {treatments.length > 0 && (
            <Row icon={Wrench} label={t("drawingRecon.treatments")} testid="recon-row-treatments">
              {treatments.slice(0, 6).map((t, i) => (
                <Tag key={`tr-${i}`} tone="amber">{t}</Tag>
              ))}
            </Row>
          )}

          {notes.length > 0 && (
            <Row icon={ScrollText} label={t("drawingRecon.drawingNotes", { count: notes.length })} testid="recon-row-notes">
              <ul className="space-y-1 w-full">
                {notes.slice(0, 5).map((n, i) => (
                  <li key={`note-${i}`} className="text-xs text-slate-400 leading-snug">
                    · {n}
                  </li>
                ))}
              </ul>
            </Row>
          )}

          {features?.ok && (
            <Row icon={Layers} label="B-Rep (OpenCascade)" testid="recon-row-brep">
              <Tag tone="cyan">
                {features.bbox_mm.length} × {features.bbox_mm.width} × {features.bbox_mm.height} mm
              </Tag>
              <Tag tone="slate">{features.volume_cm3} cm³</Tag>
              {features.geometry_classification_heuristic && (
                <Tag tone="blue">{features.geometry_classification_heuristic}</Tag>
              )}
              {(features.candidate_holes || []).length > 0 && (
                <Tag tone="emerald">
                  {t("drawingRecon.holesDetected", { count: features.candidate_holes.length })}
                </Tag>
              )}
            </Row>
          )}
        </div>
      )}

      {/* Reconciliation status footer */}
      {hasMaterialCallout && matched && selectedMaterialId === matched.id && (
        <div className="mt-3 flex items-center gap-2 text-xs text-emerald-300" data-testid="recon-status-ok">
          <CheckCircle2 size={14}/> {t("drawingRecon.coherent")}
        </div>
      )}
      {materialConflict && (
        <div className="mt-3 flex items-center gap-2 text-xs text-amber-300" data-testid="recon-status-conflict">
          <AlertTriangle size={14}/> {t("drawingRecon.materialMismatch")}
        </div>
      )}
    </div>
  );
}
